import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Globe, ArrowLeft, BookOpen, Volume2 } from 'lucide-react';
import { programAPI } from '../../services/programApi';
import { toast } from '../../hooks/use-toast';

const DayLesson = () => {
  const navigate = useNavigate();
  const { language, dayNumber } = useParams();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const isLoggedIn = !!localStorage.getItem('authToken');

  useEffect(() => {
    fetchLesson();
  }, [language, dayNumber]);

  const fetchLesson = async () => {
    try {
      const data = await programAPI.getDayLesson(language, parseInt(dayNumber));
      setLesson(data.lesson);
    } catch (error) {
      console.error('Error fetching lesson:', error);
      if (error.response?.status === 403) {
        toast({
          title: 'Day Locked',
          description: 'Complete previous days first',
          variant: 'destructive',
        });
        navigate(`/program/${language}`);
      } else if (error.response?.status === 401) {
        toast({
          title: 'Sign up to continue',
          description: 'Create a free account to unlock more lessons',
          variant: 'destructive',
        });
        navigate(`/program/${language}`);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load lesson',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      const langCodes = {
        spanish: 'es-ES',
        french: 'fr-FR',
        german: 'de-DE',
        italian: 'it-IT',
        hindi: 'hi-IN',
        tamil: 'ta-IN',
        kannada: 'kn-IN',
        telugu: 'te-IN',
        malayalam: 'ml-IN'
      };
      utterance.lang = langCodes[language] || 'en-US';
      utterance.rate = 0.8;
      
      // Wait for voices to load
      const setVoice = () => {
        const voices = window.speechSynthesis.getVoices();
        const targetLangCode = langCodes[language];
        const voiceGender = localStorage.getItem('voiceGender') || 'female';
        
        console.log('Voice selection - Gender:', voiceGender, 'Language:', language);
        
        if (voices.length > 0 && targetLangCode) {
          const langVoices = voices.filter(voice => 
            voice.lang.toLowerCase().startsWith(targetLangCode.substring(0, 2).toLowerCase())
          );
          
          if (langVoices.length > 0) {
            let selectedVoice = null;
            
            // Common female voice name patterns
            const femalePatterns = [
              'female', 'woman', 'girl',
              'samantha', 'karen', 'victoria', 'monica', 'paulina', 'fiona',
              'amelie', 'marie', 'anna', 'helena', 'petra', 'sara', 'lucia',
              'google.*female', 'microsoft.*female',
              'conchita', 'penelope', 'lupe', 'mia',
              'celine', 'lea', 'virginie',
              'marlene', 'vicki', 'hedda',
              'alice', 'elsa', 'federica', 'bianca',
              'aditi', 'raveena', 'priya', 'tanishaa', 'veena', 'sobhana'
            ];
            
            // Common male voice name patterns
            const malePatterns = [
              'male', 'man', 'boy',
              'daniel', 'thomas', 'alex', 'david', 'james', 'mark', 'fred',
              'diego', 'jorge', 'juan', 'carlos', 'enrique', 'miguel',
              'jacques', 'mathieu', 'pierre',
              'hans', 'stefan', 'markus',
              'luca', 'giorgio', 'cosimo',
              'google.*male', 'microsoft.*male',
              'ravi', 'vijay', 'karthik', 'ajit'
            ];
            
            const patterns = voiceGender === 'female' ? femalePatterns : malePatterns;
            
            // Try to find a voice matching gender preference
            for (const pattern of patterns) {
              const regex = new RegExp(pattern, 'i');
              selectedVoice = langVoices.find(v => regex.test(v.name));
              if (selectedVoice) break;
            }
            
            // If still no match, try to avoid opposite gender
            if (!selectedVoice) {
              const oppositePatterns = voiceGender === 'female' ? malePatterns : femalePatterns;
              selectedVoice = langVoices.find(v => {
                const name = v.name.toLowerCase();
                return !oppositePatterns.some(p => new RegExp(p, 'i').test(name));
              });
            }
            
            // Fallback to first available voice for the language
            utterance.voice = selectedVoice || langVoices[0];
          }
        }
        
        window.speechSynthesis.speak(utterance);
      };
      
      if (window.speechSynthesis.getVoices().length > 0) {
        setVoice();
      } else {
        window.speechSynthesis.onvoiceschanged = setVoice;
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading lesson...</p>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Lesson not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/program/${language}`)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Globe className="h-8 w-8 text-amber-600" />
                <span className="text-xl font-bold text-slate-900">
                  Day {lesson.day_number}
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Lesson Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">{lesson.title}</h1>
          <p className="text-xl text-slate-600 mb-4">{lesson.description}</p>
          <div className="flex items-center space-x-4 text-sm text-slate-600">
            <span>⏱️ {lesson.estimated_time} minutes</span>
            <span>📚 Day {lesson.day_number} of 15</span>
          </div>
        </div>

        {/* Lesson Content */}
        <Tabs defaultValue="grammar" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="grammar">Grammar</TabsTrigger>
            <TabsTrigger value="vocabulary">Vocabulary</TabsTrigger>
            <TabsTrigger value="practice">Practice</TabsTrigger>
          </TabsList>

          {/* Grammar Tab */}
          <TabsContent value="grammar" className="space-y-4">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-amber-600" />
                  Grammar Lesson
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-slate-700">
                    {lesson.content.grammar}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vocabulary Tab */}
          <TabsContent value="vocabulary" className="space-y-4">
            <Card className="border-2">
              <CardHeader>
                <CardTitle>Vocabulary ({lesson.content.vocabulary.length} words)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {lesson.content.vocabulary.map((item, index) => (
                    <div 
                      key={index}
                      className="p-4 bg-white rounded-lg border-2 hover:border-amber-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <p className="text-lg font-semibold text-slate-900">{item.word}</p>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => speakText(item.word)}
                            >
                              <Volume2 className="h-4 w-4 text-sky-600" />
                            </Button>
                          </div>
                          <p className="text-amber-700 font-medium mb-1">{item.translation}</p>
                          <p className="text-sm text-slate-600 italic">
                            Example: {item.example}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Practice Tab */}
          <TabsContent value="practice" className="space-y-4">
            {lesson.content.reading_passage && (
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>Reading Practice</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-sky-50 p-4 rounded-lg border mb-4">
                    <p className="text-lg text-slate-900 leading-relaxed">
                      {lesson.content.reading_passage}
                    </p>
                  </div>
                  <Button
                    onClick={() => speakText(lesson.content.reading_passage)}
                    variant="outline"
                  >
                    <Volume2 className="h-4 w-4 mr-2" />
                    Listen to Passage
                  </Button>
                </CardContent>
              </Card>
            )}

            {lesson.content.audio_notes && (
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>Pronunciation Tips</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">{lesson.content.audio_notes}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-between">
          <Button
            variant="outline"
            onClick={() => navigate(`/program/${language}`)}
          >
            Back to Overview
          </Button>
          {isLoggedIn ? (
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => navigate(`/program/${language}/day/${dayNumber}/test`)}
            >
              Take Day {dayNumber} Test
            </Button>
          ) : (
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => navigate('/register')}
            >
              Sign Up to Take Test
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DayLesson;